/**
 * Görev servisi — resmi @/types şemasına uygun (Gorev / GorevTamamlamasi).
 *
 * BOSS/Yönetici görev atar → personel ✓ tamamlar / ✗ yapamaz.
 * Devam eden görev: personel ✓ → durum 'beklemede' + onayBekliyor → atayan onaylar → görev 'kapandi'.
 *
 * Hedefleme (BOSS kararı, KARAR #31 kasıtlı eziliyor):
 *  - Kişi pozisyona KAYITLIYSA görür; o günkü vardiya rolü dikkate alınmaz.
 *  - Görevin saat aralığı varsa VE mesaiDisiDahil=false ise: sadece o saatlerde
 *    vardiyası olan personel görür (boş bildirim yığını engellenir).
 *  - mesaiDisiDahil=true ise saat filtresi uygulanmaz (herkesi ilgilendiren işler).
 *
 * NOT: mesaiDisiDahil, resmi şemaya BOSS onayıyla eklenen tek yeni alandır.
 *
 * Parça C eklemeleri:
 *  - kacirildiMi(): zaman aralığı geçti + işaretlenmedi → türetilmiş "kaçırıldı"
 *    durumu. DB'ye YAZILMAZ (KARAR #24). Rozet/Takip için saf hesap.
 *  - Bildirim: tamamla/yapilamadi → atayana; reddet → personele;
 *    önce yapamamış/reddedilmiş görev tekrar yapıldı → atayana "geri alındı".
 *    (Sadece sistem-içi — KARAR #22.)
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where,
} from 'firebase/firestore'
import {
  getStorage, ref as storageRef, uploadBytes, getDownloadURL,
} from 'firebase/storage'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type {
  Gorev, GorevTipi, GorevHedefi, GorevTamamlamasi, IsoTarih,
  KullaniciPersonel,
} from '@/types'
import type { Vardiya } from './vardiya-service'
import { personelGelecekVardiyalariGetir, personelGununkuVardiyalari } from './vardiya-service'
import { logYaz, type LogRol } from './sistem-log-service'
import { bildirimGonder, type BildirimTipiGenis } from './bildirim-service'

const GOREVLER = 'gorevler'
const TAMAMLAMALAR = 'gorevTamamlamalari'

/** Resmi şemaya eklenen alanlar — Gorev tipinde yok, doc'ta saklanır. */
export interface GorevEk {
  mesaiDisiDahil?: boolean
  /** Paylaşımlı (vardiya) görev: hedeften biri yaparsa biter, kişi bazlı nag yok. */
  paylasimli?: boolean
}
export type GorevDoc = Gorev & GorevEk

const iso = (s: string) => s as unknown as IsoTarih

function bugunStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
}

function gunBilgi(s: string) {
  const [y, m, d] = s.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return { dt, dow: dt.getDay(), dom: d }
}

/** "YYYY-MM-DD" → bir önceki gün ("YYYY-MM-DD"). */
export function oncekiGun(tarih: string): string {
  const { dt } = gunBilgi(tarih)
  const p = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - 1)
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`
}

/** "HH:MM" → dakika. */
function dk(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/**
 * Bir görevin o tarihteki BİTİŞ anı geçti mi (gece yarısı taşması dahil).
 * tarih = "YYYY-MM-DD", za = görevin zamanAraligi.
 */
function gorevZamaniGecti(
  tarih: string,
  za: { baslangic: string; bitis: string },
  simdi: Date
): boolean {
  const [y, m, d] = tarih.split('-').map(Number)
  const bSt = dk(za.baslangic)
  const bIt = dk(za.bitis)
  const ekGun = bIt <= bSt ? 1 : 0 // gece yarısını aşan vardiya → ertesi gün biter
  const bitis = new Date(y, m - 1, d + ekGun, Math.floor(bIt / 60), bIt % 60, 0, 0)
  return simdi.getTime() > bitis.getTime()
}

/** Tamamlama doküman ID'si — idempotency için sabit. */
export function tamamlamaId(
  gorevId: string, personelId: string, tipi: GorevTipi, tarih: string
): string {
  return tipi === 'devamEden'
    ? `${gorevId}_${personelId}`
    : `${gorevId}_${personelId}_${tarih}`
}

/** Tam saat (başlangıç = bitiş) görev için tamamlama toleransı (dk). */
export const GOREV_TOLERANS_DK = 15

export type TamamlanabilirSonuc =
  | { olur: true }
  | { olur: false; sebep: 'erken' | 'gecti'; mesaj: string }

/**
 * Saat aralığı olan görev ne zaman "yapıldı" işaretlenebilir?
 *  - Aralık (başlangıç ≠ bitiş): BİTİŞE kadar (erken sınır yok). Bitiş geçince → 'gecti'.
 *  - Tam saat (başlangıç = bitiş): ±GOREV_TOLERANS_DK dk.
 *  - Saat aralığı yoksa veya devamEden: her zaman yapılabilir.
 */
export function gorevTamamlanabilirMi(
  g: Pick<GorevDoc, 'tipi' | 'zamanAraligi'>,
  tarih: string,
  simdi: Date = new Date()
): TamamlanabilirSonuc {
  const za = g.zamanAraligi
  if (!za) return { olur: true }
  if (g.tipi === 'devamEden') return { olur: true }

  const [y, m, d] = tarih.split('-').map(Number)
  const bSt = dk(za.baslangic)
  const bIt = dk(za.bitis)
  const now = simdi.getTime()

  // Tam saat → ± tolerans
  if (bSt === bIt) {
    const anMs = new Date(y!, m! - 1, d!, Math.floor(bSt / 60), bSt % 60, 0, 0).getTime()
    const tolMs = GOREV_TOLERANS_DK * 60000
    if (now < anMs - tolMs) {
      return { olur: false, sebep: 'erken', mesaj: `Bu görev ${za.baslangic} civarında yapılabilir (±${GOREV_TOLERANS_DK} dk).` }
    }
    if (now > anMs + tolMs) {
      return { olur: false, sebep: 'gecti', mesaj: `Bu görevin süresi geçti (${za.baslangic}).` }
    }
    return { olur: true }
  }

  // Aralık → bitişe kadar (gece yarısı taşması dahil), erken sınır yok
  const ekGun = bIt <= bSt ? 1 : 0
  const bitisMs = new Date(y!, m! - 1, d! + ekGun, Math.floor(bIt / 60), bIt % 60, 0, 0).getTime()
  if (now > bitisMs) {
    return { olur: false, sebep: 'gecti', mesaj: `Bu görevin süresi ${za.bitis}'de doldu.` }
  }
  return { olur: true }
}

// ───────────────── bildirim yardımcıları (Parça C) ─────────────────

/** Bildirim hatası ASLA görev akışını bozmasın (best-effort). */
async function bildirimGuvenli(
  fn: () => Promise<unknown>
): Promise<void> {
  try {
    await fn()
  } catch (e) {
    console.error('Görev bildirimi gönderilemedi:', e)
  }
}

async function gorevAtayanBildir(
  gorevId: string,
  tipi: BildirimTipiGenis,
  baslik: string,
  mesaj: string
): Promise<void> {
  await bildirimGuvenli(async () => {
    const g = await gorevGetir(gorevId)
    if (!g?.atayanId) return
    await bildirimGonder({
      kullaniciId: g.atayanId,
      tipi,
      baslik,
      mesaj,
      link: '/boss/gorevler',
      ilgiliKayit: { koleksiyon: GOREVLER, id: gorevId },
    })
  })
}

/**
 * Bu görev/personel için (tarih kapsamında) daha önce
 * 'yapilamadi' veya 'reddedildi' kaydı var mı? → "geri alma" tespiti.
 */
async function oncedenCozulememisMi(
  gorevId: string,
  personelId: string,
  tipi: GorevTipi,
  tarih: string
): Promise<boolean> {
  try {
    const hepsi = await personelTamamlamalari(personelId)
    return hepsi.some((t) => {
      if (t.gorevId !== gorevId) return false
      const ayniKapsam =
        tipi === 'devamEden' ? true : (t.tarih as unknown as string) === tarih
      if (!ayniKapsam) return false
      const d = t.durum as string
      return d === 'yapilamadi' || d === 'reddedildi'
    })
  } catch {
    return false
  }
}

// ───────────────────────── görev CRUD ─────────────────────────

export interface GorevOlusturData {
  baslik: string
  aciklama: string
  tipi: GorevTipi
  hedef: GorevHedefi
  baslangicTarihi: string
  bitisTarihi?: string
  zamanAraligi?: { baslangic: string; bitis: string }
  mesaiDisiDahil?: boolean
  paylasimli?: boolean
  fotoZorunlu?: boolean
  aciklamaZorunlu?: boolean
  atayanId: string
  atayanRol: 'boss' | 'sorumlu'
  yapanIsim?: string
}

function hedefTemizle(h: GorevHedefi): GorevHedefi {
  return {
    personelIdleri: h.personelIdleri ?? [],
    pozisyonIdleri: h.pozisyonIdleri ?? [],
    subeIdleri: h.subeIdleri ?? [],
  }
}

function hedefBos(h: GorevHedefi): boolean {
  return (
    (h.personelIdleri?.length ?? 0) === 0 &&
    (h.pozisyonIdleri?.length ?? 0) === 0 &&
    (h.subeIdleri?.length ?? 0) === 0
  )
}

export async function gorevOlustur(data: GorevOlusturData): Promise<string> {
  if (!data.baslik?.trim()) throw new Error('Görev başlığı zorunlu.')
  if (!data.aciklama?.trim()) throw new Error('Görev açıklaması zorunlu.')
  if (!data.baslangicTarihi) throw new Error('Başlangıç tarihi zorunlu.')

  const hedef = hedefTemizle(data.hedef)
  if (hedefBos(hedef)) {
    throw new Error('Görev en az bir kişiye, pozisyona veya şubeye atanmalı.')
  }
  if (data.bitisTarihi && data.bitisTarihi < data.baslangicTarihi) {
    throw new Error('Bitiş tarihi başlangıçtan önce olamaz.')
  }

  const db = getFirestoreDb()
  const id = doc(collection(db, GOREVLER)).id

  const docData: Record<string, unknown> = {
    baslik: data.baslik.trim(),
    aciklama: data.aciklama.trim(),
    tipi: data.tipi,
    hedef,
    baslangicTarihi: data.baslangicTarihi,
    fotoZorunlu: data.fotoZorunlu ?? false,
    aciklamaZorunlu: data.aciklamaZorunlu ?? false,
    mesaiDisiDahil: data.mesaiDisiDahil ?? false,
    paylasimli: data.paylasimli ?? false,
    atayanId: data.atayanId,
    atayanRol: data.atayanRol,
    durum: 'aktif',
    olusturmaTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  }
  if (data.bitisTarihi) docData.bitisTarihi = data.bitisTarihi
  if (data.zamanAraligi?.baslangic && data.zamanAraligi?.bitis) {
    docData.zamanAraligi = data.zamanAraligi
  }

  await setDoc(doc(db, GOREVLER, id), docData)

  await logYaz({
    tip: 'gorev.olustur',
    baslik: `Görev oluşturuldu: ${data.baslik.trim()}`,
    hedefId: id,
    yapanId: data.atayanId,
    yapanIsim: data.yapanIsim,
    yapanRol: data.atayanRol === 'boss' ? 'boss' : 'sorumlu',
    ekBilgi: { gorevTipi: data.tipi },
  })
  return id
}

export async function gorevGuncelle(
  id: string,
  data: Partial<GorevOlusturData>,
  yapanId: string,
  yapanIsim?: string,
  yapanRol: LogRol = 'boss'
): Promise<void> {
  const db = getFirestoreDb()
  const ref = doc(db, GOREVLER, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Görev bulunamadı.')

  const g: Record<string, unknown> = { guncellenmeTarihi: nowServer() }

  if (data.baslik != null) {
    if (!data.baslik.trim()) throw new Error('Başlık boş olamaz.')
    g.baslik = data.baslik.trim()
  }
  if (data.aciklama != null) {
    if (!data.aciklama.trim()) throw new Error('Açıklama boş olamaz.')
    g.aciklama = data.aciklama.trim()
  }
  if (data.tipi != null) g.tipi = data.tipi
  if (data.hedef != null) {
    const h = hedefTemizle(data.hedef)
    if (hedefBos(h)) throw new Error('Görev en az bir hedefe atanmalı.')
    g.hedef = h
  }
  if (data.baslangicTarihi != null) g.baslangicTarihi = data.baslangicTarihi
  if (data.bitisTarihi !== undefined) g.bitisTarihi = data.bitisTarihi
  if (data.zamanAraligi !== undefined) g.zamanAraligi = data.zamanAraligi
  if (data.mesaiDisiDahil !== undefined) g.mesaiDisiDahil = data.mesaiDisiDahil
  if (data.paylasimli !== undefined) g.paylasimli = data.paylasimli
  if (data.fotoZorunlu !== undefined) g.fotoZorunlu = data.fotoZorunlu
  if (data.aciklamaZorunlu !== undefined) g.aciklamaZorunlu = data.aciklamaZorunlu

  await updateDoc(ref, g)

  await logYaz({
    tip: 'gorev.guncelle',
    baslik: `Görev güncellendi: ${(snap.data() as Gorev).baslik}`,
    hedefId: id,
    yapanId, yapanIsim, yapanRol,
  })
}

export async function gorevArsivle(
  id: string, yapanId: string, yapanIsim?: string, yapanRol: LogRol = 'boss'
): Promise<void> {
  const db = getFirestoreDb()
  const ref = doc(db, GOREVLER, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Görev bulunamadı.')
  await updateDoc(ref, { durum: 'arsivlendi', guncellenmeTarihi: nowServer() })
  await logYaz({
    tip: 'gorev.arsivle',
    baslik: `Görev arşivlendi: ${(snap.data() as Gorev).baslik}`,
    hedefId: id, yapanId, yapanIsim, yapanRol,
  })
}

export async function gorevAktiflestir(id: string): Promise<void> {
  await updateDoc(doc(getFirestoreDb(), GOREVLER, id), {
    durum: 'aktif', guncellenmeTarihi: nowServer(),
  })
}

export async function gorevSil(
  id: string, yapanId: string, yapanIsim?: string, yapanRol: LogRol = 'boss'
): Promise<void> {
  const db = getFirestoreDb()
  const ref = doc(db, GOREVLER, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Görev bulunamadı.')
  const veri = snap.data() as Gorev
  await deleteDoc(ref)
  await logYaz({
    tip: 'gorev.sil',
    baslik: `Görev silindi: ${veri.baslik}`,
    hedefId: id, yapanId, yapanIsim, yapanRol,
    ekBilgi: { gorevTipi: veri.tipi },
  })
}

export async function gorevGetir(id: string): Promise<GorevDoc | null> {
  const snap = await getDoc(doc(getFirestoreDb(), GOREVLER, id))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as GorevDoc
}

export async function tumGorevler(): Promise<GorevDoc[]> {
  const snap = await getDocs(collection(getFirestoreDb(), GOREVLER))
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as GorevDoc)
    .sort(
      (a, b) =>
        (b.olusturmaTarihi?.toMillis?.() ?? 0) -
        (a.olusturmaTarihi?.toMillis?.() ?? 0)
    )
}

// ───────────────── resolution (kime, hangi gün) ─────────────────

/** Görev bu tarihte (takvim olarak) aktif mi? */
export function gorevTarihteAktifMi(g: GorevDoc, tarih: string): boolean {
  if (g.durum !== 'aktif') return false
  if (tarih < g.baslangicTarihi) return false
  if (g.bitisTarihi && tarih > g.bitisTarihi) return false

  const B = gunBilgi(g.baslangicTarihi)
  const D = gunBilgi(tarih)
  switch (g.tipi) {
    case 'tekSeferlik': return tarih === g.baslangicTarihi
    case 'rutinGunluk': return true
    case 'rutinHaftalik': return D.dow === B.dow
    case 'rutinAylik': return D.dom === B.dom
    case 'devamEden': return true
    default: return false
  }
}

/**
 * Personel görevin hedefiyle eşleşiyor mu.
 *
 * Kural:
 *  - Tek tek seçilmiş kişi (personelIdleri) → her zaman dahil.
 *  - Pozisyon VE şube birlikte seçiliyse → kişi seçili pozisyonlardan birinde
 *    OLMALI **VE** seçili şubelerden birinde OLMALI (şube kapsamı daraltır).
 *  - Sadece pozisyon seçiliyse → o pozisyondaki herkes (şube fark etmez).
 *  - Sadece şube seçiliyse → o şubelerdeki herkes.
 */
export function personelHedefeUyuyorMu(
  g: GorevDoc, p: Pick<KullaniciPersonel, 'id' | 'pozisyonIdleri' | 'subeIdleri'>
): boolean {
  const h = g.hedef
  // 1) Tek tek seçilmiş kişi → her zaman
  if (h.personelIdleri?.includes(p.id)) return true

  const pozVar = (h.pozisyonIdleri?.length ?? 0) > 0
  const subeVar = (h.subeIdleri?.length ?? 0) > 0

  const pozUyar = pozVar
    ? !!h.pozisyonIdleri?.some((pid) => p.pozisyonIdleri?.includes(pid))
    : false
  const subeUyar = subeVar
    ? !!h.subeIdleri?.some((sid) => p.subeIdleri?.includes(sid))
    : false

  // 2) Pozisyon + şube birlikte → ikisi de uymalı (şube daraltır)
  if (pozVar && subeVar) return pozUyar && subeUyar
  // 3) Sadece pozisyon
  if (pozVar) return pozUyar
  // 4) Sadece şube
  if (subeVar) return subeUyar

  return false
}

/**
 * Görevin saat aralığı varsa ve mesaiDisiDahil=false ise,
 * personelin o saatleri kapsayan vardiyası olmalı.
 * Gece vardiyası bir önceki güne yazıldığı için (örn. 22:00–08:00),
 * sabaha sarkan kısım da hesaba katılır: bugün + dün vardiyaları,
 * gün D 00:00'a göre mutlak dakika örtüşmesiyle kontrol edilir.
 */
export function mesaiFiltresiGecer(
  g: GorevDoc, tarih: string, personelVardiyalari: Vardiya[]
): boolean {
  if (!g.zamanAraligi) return true
  if (g.mesaiDisiDahil) return true

  const onceki = oncekiGun(tarih)
  const adaylar = personelVardiyalari.filter(
    (v) => (v.tarih === tarih || v.tarih === onceki) && v.durum !== 'izinli'
  )
  if (adaylar.length === 0) return false

  // Görev penceresi — gün D üzerinde mutlak dakika [ts, te]
  const ts = dk(g.zamanAraligi.baslangic)
  let te = dk(g.zamanAraligi.bitis)
  if (te <= ts) te += 1440 // gece yarısını aşan görev penceresi

  return adaylar.some((v) => {
    const gunFark = v.tarih === onceki ? -1 : 0
    const vs = gunFark * 1440 + dk(v.baslangic)
    let vdur = dk(v.bitis) - dk(v.baslangic)
    if (vdur <= 0) vdur += 1440 // gece vardiyası ertesi güne sarkar
    const ve = vs + vdur
    return vs < te && ts < ve
  })
}

/**
 * KAÇIRILDI türetmesi (KARAR #24 — DB'ye yazılmaz, saf hesap).
 *
 * Bir görev/personel/gün için:
 *  - zamanAraligi YOKSA → kaçırma kavramı yok (false).
 *  - devamEden → kapanana kadar açık (false).
 *  - görev artık 'aktif' değilse → false (kapandı/arşiv).
 *  - o tarihte takvimsel aktif değilse → false.
 *  - son tamamlama tamamlandi/beklemede/yapilamadi ise → çözülmüş (false).
 *    ('reddedildi' → hâlâ açık, süre geçtiyse kaçırıldı sayılır.)
 *  - görevin o tarihteki bitiş anı geçtiyse → true.
 */
export function kacirildiMi(
  g: GorevDoc,
  tarih: string,
  sonTamamlama: GorevTamamlamasi | null,
  simdi: Date = new Date()
): boolean {
  if (!g.zamanAraligi) return false
  if (g.tipi === 'devamEden') return false
  if (g.durum !== 'aktif') return false
  if (!gorevTarihteAktifMi(g, tarih)) return false
  if (sonTamamlama) {
    const d = sonTamamlama.durum as string
    if (d === 'tamamlandi' || d === 'beklemede' || d === 'yapilamadi') return false
    // 'reddedildi' → açık kabul, süre kontrolüne devam
  }
  return gorevZamaniGecti(tarih, g.zamanAraligi, simdi)
}

export interface PersonelGorevSatiri {
  gorev: GorevDoc
  tamamlama: GorevTamamlamasi | null
  tarih: string
}

/** Bir personelin bugün görmesi gereken görevleri (tamamlama durumu dahil). */
export async function personelBugunkuGorevleri(
  personel: Pick<KullaniciPersonel, 'id' | 'pozisyonIdleri' | 'subeIdleri'>
): Promise<PersonelGorevSatiri[]> {
  const today = bugunStr()
  const [hepsi, vardiyalar, dunVardiyalari, tamamlamalar] = await Promise.all([
    tumGorevler(),
    personelGelecekVardiyalariGetir(personel.id),
    personelGununkuVardiyalari(personel.id, oncekiGun(today)),
    personelTamamlamalari(personel.id),
  ])
  // Dünden sarkan gece vardiyaları da katılır (mesaiFiltresiGecer bunları kullanır).
  const tumVardiya = [...vardiyalar, ...dunVardiyalari]

  const sonuc: PersonelGorevSatiri[] = []
  for (const g of hepsi) {
    if (!gorevTarihteAktifMi(g, today)) continue
    if (!personelHedefeUyuyorMu(g, personel)) continue
    if (!mesaiFiltresiGecer(g, today, tumVardiya)) continue
    const t = aktifTamamlama(tamamlamalar, g.id, personel.id, g.tipi, today)
    sonuc.push({ gorev: g, tamamlama: t, tarih: today })
  }
  return sonuc.sort((a, b) => {
    const ad = acikMi(a.tamamlama) ? 0 : 1
    const bd = acikMi(b.tamamlama) ? 0 : 1
    return ad - bd
  })
}

/**
 * Bir görev/personel/gün için EN SON denemeyi döner (çok-deneme modeli).
 * Üzerine yazmıyoruz — her deneme ayrı kayıt; durum son denemeye bakar.
 */
export function aktifTamamlama(
  tumTamamlama: GorevTamamlamasi[],
  gorevId: string,
  personelId: string,
  tipi: GorevTipi,
  tarih: string
): GorevTamamlamasi | null {
  const ilgili = tumTamamlama.filter((t) => {
    if (t.gorevId !== gorevId || t.personelId !== personelId) return false
    // devamEden: tarih bağımsız (görev bitene kadar). Diğerleri: aynı gün.
    return tipi === 'devamEden' ? true : (t.tarih as unknown as string) === tarih
  })
  if (ilgili.length === 0) return null
  ilgili.sort(
    (a, b) =>
      (b.olusturmaTarihi?.toMillis?.() ?? 0) -
      (a.olusturmaTarihi?.toMillis?.() ?? 0)
  )
  return ilgili[0]
}

/** Görev personelde "açık" mı (yeni deneme yapılabilir mi)? */
export function acikMi(t: GorevTamamlamasi | null): boolean {
  if (!t) return true
  const d = t.durum as string
  return d === 'reddedildi' || d === 'kacirildi'
}

// ───────────── Paylaşımlı (vardiya) görev çekirdeği ─────────────
// paylasimli=true: görevi hedef/mesai eşleşen HERHANGİ BİR kişi tamamlarsa biter.
// Kişi bazlı "tamamlamadın" uyarısı YOK; kimse yapmazsa TEK kaçırma bildirimi.

export function gorevPaylasimliMi(g: GorevDoc): boolean {
  return !!g.paylasimli
}

/** Görev+tarih için HERHANGİ biri 'tamamlandi' yapmış mı → o tamamlamayı döner (yoksa null). */
export function paylasimliTamamlama(
  gorevId: string,
  tarih: string,
  tipi: GorevTipi,
  tumTamamlama: GorevTamamlamasi[]
): GorevTamamlamasi | null {
  const ilgili = tumTamamlama.filter((t) => {
    if (t.gorevId !== gorevId) return false
    if ((t.durum as string) !== 'tamamlandi') return false
    return tipi === 'devamEden' ? true : (t.tarih as unknown as string) === tarih
  })
  if (ilgili.length === 0) return null
  ilgili.sort(
    (a, b) =>
      (b.olusturmaTarihi?.toMillis?.() ?? 0) -
      (a.olusturmaTarihi?.toMillis?.() ?? 0)
  )
  return ilgili[0]!
}

export type PaylasimliDurum = 'tamamlandi' | 'kacirildi' | 'bekliyor'

/**
 * Paylaşımlı görevin o günkü TEK durumu (kişi bazlı değil, görev bazlı):
 *  - biri tamamladıysa → 'tamamlandi'
 *  - kimse yapmadı + o tarihteki bitiş anı geçti → 'kacirildi'
 *  - aksi hâlde → 'bekliyor'
 */
export function paylasimliGunDurumu(
  g: GorevDoc,
  tarih: string,
  tumTamamlama: GorevTamamlamasi[],
  simdi: Date = new Date()
): PaylasimliDurum {
  if (paylasimliTamamlama(g.id, tarih, g.tipi, tumTamamlama)) return 'tamamlandi'
  if (g.durum !== 'aktif') return 'bekliyor'
  if (!gorevTarihteAktifMi(g, tarih)) return 'bekliyor'
  if (g.tipi === 'devamEden') return 'bekliyor'
  if (g.zamanAraligi && gorevZamaniGecti(tarih, g.zamanAraligi, simdi)) return 'kacirildi'
  return 'bekliyor'
}

// ───────────────────────── tamamlama ─────────────────────────

export async function gorevTamamlamalari(gorevId: string): Promise<GorevTamamlamasi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, TAMAMLAMALAR), where('gorevId', '==', gorevId))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GorevTamamlamasi)
}

export async function personelTamamlamalari(
  personelId: string
): Promise<GorevTamamlamasi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, TAMAMLAMALAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GorevTamamlamasi)
}

/** Foto sıkıştır (max 1400px, jpeg 0.7) — spec 16.4. */
async function resimSikistir(file: File): Promise<Blob> {
  const img = document.createElement('img')
  const url = URL.createObjectURL(file)
  try {
    await new Promise<void>((res, rej) => {
      img.onload = () => res()
      img.onerror = () => rej(new Error('Görsel okunamadı.'))
      img.src = url
    })
    const maxW = 1400
    const oran = img.width > maxW ? maxW / img.width : 1
    const c = document.createElement('canvas')
    c.width = Math.round(img.width * oran)
    c.height = Math.round(img.height * oran)
    const ctx = c.getContext('2d')
    if (!ctx) throw new Error('Canvas desteklenmiyor.')
    ctx.drawImage(img, 0, 0, c.width, c.height)
    return await new Promise<Blob>((res, rej) =>
      c.toBlob(
        (b) => (b ? res(b) : rej(new Error('Sıkıştırma başarısız.'))),
        'image/jpeg',
        0.7
      )
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function gorevFotoYukle(
  gorevId: string, personelId: string, tarih: string, file: File
): Promise<string> {
  const blob = await resimSikistir(file)
  const storage = getStorage()
  // Benzersiz isim — aynı gün birden çok foto üst üste yazılmasın (çoklu foto).
  const benzersiz = `${tarih}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const yol = `gorevFotolari/${gorevId}/${personelId}/${benzersiz}.jpg`
  const r = storageRef(storage, yol)
  await uploadBytes(r, blob, { contentType: 'image/jpeg' })
  return await getDownloadURL(r)
}

export async function gorevTamamla(
  gorev: Pick<GorevDoc, 'id' | 'tipi' | 'aciklamaZorunlu' | 'fotoZorunlu' | 'zamanAraligi'>,
  personelId: string,
  opts: { tarih?: string; aciklama?: string; fotoUrllar?: string[]; gecikmeli?: boolean } = {}
): Promise<void> {
  const tarih = opts.tarih ?? bugunStr()

  // Zaman penceresi: erken tamamlama daima engelli; süresi geçmişse
  // yalnız bilinçli "yine de tamamla" (gecikmeli) ile izin verilir.
  const pencere = gorevTamamlanabilirMi(gorev, tarih)
  if (!pencere.olur) {
    if (pencere.sebep === 'erken' || !opts.gecikmeli) {
      throw new Error(pencere.mesaj)
    }
  }

  if (gorev.aciklamaZorunlu && !opts.aciklama?.trim()) {
    throw new Error('Bu görev için açıklama zorunlu.')
  }
  if (gorev.fotoZorunlu && (!opts.fotoUrllar || opts.fotoUrllar.length === 0)) {
    throw new Error('Bu görev için fotoğraf zorunlu.')
  }

  // "Geri alma" tespiti — yazmadan ÖNCE bak (yeni kayıt sonucu bozmasın).
  const geriAlma = await oncedenCozulememisMi(
    gorev.id, personelId, gorev.tipi, tarih
  )

  const db = getFirestoreDb()
  // Her deneme AYRI kayıt — üzerine yazma yok (geçmiş + fotolar korunur).
  const id = doc(collection(db, TAMAMLAMALAR)).id
  const devam = gorev.tipi === 'devamEden'

  const docData: Record<string, unknown> = {
    gorevId: gorev.id,
    personelId,
    tarih,
    durum: devam ? 'beklemede' : 'tamamlandi',
    onayBekliyor: devam,
    olusturmaTarihi: nowServer(),
  }
  if (opts.aciklama?.trim()) docData.aciklama = opts.aciklama.trim()
  if (opts.fotoUrllar && opts.fotoUrllar.length > 0) docData.fotoUrllar = opts.fotoUrllar

  await setDoc(doc(db, TAMAMLAMALAR, id), docData)

  // Tamamlama bildirimleri Cloud Function trigger ile gönderilir
  // (gorevTamamlamaTrigger). Burada manuel çağrı yok — düplikasyonu önler
  // ve zengin içerik (PersonelIsim + GorevBaslik + foto + açıklama) sunucu
  // tarafında üretilir.
}

export async function gorevYapilamadi(
  gorev: Pick<GorevDoc, 'id' | 'tipi'>,
  personelId: string,
  aciklama: string,
  opts: { tarih?: string; fotoUrllar?: string[] } = {}
): Promise<void> {
  if (!aciklama?.trim()) throw new Error('Yapılamadı için açıklama zorunlu.')
  const tarih = opts.tarih ?? bugunStr()
  const db = getFirestoreDb()
  const id = doc(collection(db, TAMAMLAMALAR)).id
  const docData: Record<string, unknown> = {
    gorevId: gorev.id,
    personelId,
    tarih,
    durum: 'yapilamadi',
    onayBekliyor: false,
    aciklama: aciklama.trim(),
    olusturmaTarihi: nowServer(),
  }
  if (opts.fotoUrllar && opts.fotoUrllar.length > 0) docData.fotoUrllar = opts.fotoUrllar
  await setDoc(doc(db, TAMAMLAMALAR, id), docData)

  await gorevAtayanBildir(
    gorev.id,
    'gorevYapilamadi',
    '⚠️ Görev yapılamadı',
    `Sebep: ${aciklama.trim()}`
  )
}

/** Devam eden görev: atayan onaylar → görev 'kapandi'. */
export async function gorevKapatmaOnayla(
  tamamlamaIdStr: string, onaylayanId: string
): Promise<void> {
  const db = getFirestoreDb()
  const tRef = doc(db, TAMAMLAMALAR, tamamlamaIdStr)
  const tSnap = await getDoc(tRef)
  if (!tSnap.exists()) throw new Error('Tamamlama kaydı bulunamadı.')
  const t = tSnap.data() as GorevTamamlamasi
  if (!t.onayBekliyor) throw new Error('Bu kayıt onay beklemiyor.')
  await updateDoc(tRef, {
    durum: 'tamamlandi',
    onayBekliyor: false,
    onaylayanId,
    onayTarihi: nowServer(),
  })
  await updateDoc(doc(db, GOREVLER, t.gorevId), {
    durum: 'kapandi',
    kapatanId: onaylayanId,
    kapanmaTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  })
}

/**
 * Bir tamamlamayı REDDET — her görev tipinde geçerli.
 * Kayıt SİLİNMEZ: durum 'reddedildi' + sebep yazılır (geçmiş + foto korunur).
 * Görev personelde yeniden açılır (yeni deneme yapılabilir).
 * Devam eden görevse kapanmaz (zaten kapanmamıştı).
 */
export async function gorevTamamlamaReddet(
  tamamlamaIdStr: string,
  redEdenId: string,
  redSebebi: string
): Promise<void> {
  if (!redSebebi?.trim()) throw new Error('Red sebebi zorunlu.')
  const db = getFirestoreDb()
  const tRef = doc(db, TAMAMLAMALAR, tamamlamaIdStr)
  const tSnap = await getDoc(tRef)
  if (!tSnap.exists()) throw new Error('Tamamlama kaydı bulunamadı.')
  const t = tSnap.data() as GorevTamamlamasi
  await updateDoc(tRef, {
    durum: 'reddedildi',
    onayBekliyor: false,
    onaylayanId: redEdenId,
    onayTarihi: nowServer(),
    redSebebi: redSebebi.trim(),
  })

  // Bildirim → personele (görevin tekrar yapılması gerekiyor).
  await bildirimGuvenli(async () => {
    const g = await gorevGetir(t.gorevId)
    await bildirimGonder({
      kullaniciId: t.personelId,
      tipi: 'gorevReddedildi',
      baslik: '🔄 Görevin geri gönderildi',
      mesaj: `${g?.baslik ? `"${g.baslik}" ` : ''}reddedildi: ${redSebebi.trim()}`,
      link: '/personel',
      ilgiliKayit: { koleksiyon: GOREVLER, id: t.gorevId },
    })
  })
}

/** Geriye dönük uyumluluk — eski çağrılar sebepsiz reddetmesin diye sebep zorunlu. */
export async function gorevKapatmaReddet(
  tamamlamaIdStr: string,
  redEdenId: string,
  redSebebi: string
): Promise<void> {
  await gorevTamamlamaReddet(tamamlamaIdStr, redEdenId, redSebebi)
}

// ───────────── BOSS takip: kim hedeflendi + kim ne yaptı ─────────────

export async function tumTamamlamalar(): Promise<GorevTamamlamasi[]> {
  const snap = await getDocs(collection(getFirestoreDb(), TAMAMLAMALAR))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GorevTamamlamasi)
}

export type KisiDurum =
  | 'tamamlandi' | 'yapilamadi' | 'beklemede' | 'reddedildi'
  | 'kacirildi' | 'bekliyor'

export interface GorevKisiDurum {
  personel: KullaniciPersonel
  tamamlama: GorevTamamlamasi | null
  durum: KisiDurum
  /** O görev/personel/gün için tüm denemeler (yeni → eski). */
  denemeler: GorevTamamlamasi[]
}

/** Görevin kart tarihi: tekSeferlik → kendi başlangıç tarihi, diğerleri → bugün. */
export function gorevKartTarihi(g: GorevDoc): string {
  if (g.tipi === 'tekSeferlik') {
    const bt = g.baslangicTarihi as unknown as string
    return bt && bt.includes('-') ? bt : bugunStr()
  }
  return bugunStr()
}

/** Bir tarihin haftasının Pazartesi'si (vardiya haftaBaslangic ile uyumlu). */
export function haftaBasiHesapla(tarih: string): string {
  const [y, m, d] = tarih.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  const gun = dt.getDay() // 0=Pazar
  const fark = gun === 0 ? -6 : 1 - gun
  dt.setDate(dt.getDate() + fark)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(
    dt.getDate()
  ).padStart(2, '0')}`
}

/**
 * Bir görevin BELİRLİ bir tarihte hedeflediği TÜM personel + her birinin durumu.
 * Saf fonksiyon — veri çekmez (sayfa bir kez çekip her görev için çağırır).
 * Pozisyon/şube hedefi otomatik olarak gerçek kişilere açılır.
 *
 * Parça C: deneme yoksa ve görevin o tarihteki süresi geçtiyse → 'kacirildi'.
 */
export function gorevGununDurumu(
  gorev: GorevDoc,
  tarih: string,
  tumPersonel: KullaniciPersonel[],
  tarihVardiyalari: Vardiya[],
  tumTamamlama: GorevTamamlamasi[],
  simdi: Date = new Date()
): GorevKisiDurum[] {
  if (!gorevTarihteAktifMi(gorev, tarih)) return []
  const sonuc: GorevKisiDurum[] = []
  for (const p of tumPersonel) {
    if (p.aktif === false) continue
    if (!personelHedefeUyuyorMu(gorev, p)) continue
    const pVardiya = tarihVardiyalari.filter((v) => v.personelId === p.id)
    if (!mesaiFiltresiGecer(gorev, tarih, pVardiya)) continue

    const denemeler = tumTamamlama
      .filter((x) => {
        if (x.gorevId !== gorev.id || x.personelId !== p.id) return false
        return gorev.tipi === 'devamEden'
          ? true
          : (x.tarih as unknown as string) === tarih
      })
      .sort(
        (a, b) =>
          (b.olusturmaTarihi?.toMillis?.() ?? 0) -
          (a.olusturmaTarihi?.toMillis?.() ?? 0)
      )

    const t = denemeler[0] ?? null
    let durum: KisiDurum = 'bekliyor'
    if (t) {
      const d = t.durum as string
      if (d === 'tamamlandi') durum = 'tamamlandi'
      else if (d === 'yapilamadi') durum = 'yapilamadi'
      else if (d === 'reddedildi') durum = 'reddedildi'
      else if (d === 'beklemede' || t.onayBekliyor) durum = 'beklemede'
    }
    // Çözülmemiş + süresi geçmiş → kaçırıldı (KARAR #24, türetilmiş).
    if (
      (durum === 'bekliyor' || durum === 'reddedildi') &&
      kacirildiMi(gorev, tarih, t, simdi)
    ) {
      durum = 'kacirildi'
    }
    sonuc.push({ personel: p, tamamlama: t, durum, denemeler })
  }
  return sonuc.sort((a, b) =>
    (a.personel.isim ?? '').localeCompare(b.personel.isim ?? '', 'tr')
  )
}

export { iso }
